import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin control for the estimate Risks badge thresholds. */
export const RiskSettingsCard = () => {
  const [countThr, setCountThr] = useState(4);
  const [hoursThr, setHoursThr] = useState(120);
  const [govThr, setGovThr] = useState(1.1);
  const [tlThr, setTlThr] = useState(1.0);
  const [diMedThr, setDiMedThr] = useState(1.0);
  const [diHighThr, setDiHighThr] = useState(1.2);
  const [stdText, setStdText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  const recalc = async () => {
    setRecalcing(true);
    try {
      const res = await axios.post(`${API_URL}/api/settings/risk/recalculate`, {}, { withCredentials: true });
      toast.success(`Risk badges refreshed on ${res.data.updated} estimates${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not recalculate risk badges');
    } finally {
      setRecalcing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/risk`, { withCredentials: true });
      setCountThr(res.data.integration_count_threshold);
      setHoursThr(res.data.integration_hours_threshold);
      if (res.data.governance_threshold != null) setGovThr(res.data.governance_threshold);
      if (res.data.timeline_threshold != null) setTlThr(res.data.timeline_threshold);
      if (res.data.data_integrity_medium_threshold != null) setDiMedThr(res.data.data_integrity_medium_threshold);
      if (res.data.data_integrity_high_threshold != null) setDiHighThr(res.data.data_integrity_high_threshold);
      setStdText((res.data.standard_integrations || []).join('\n'));
    } catch {
      // defaults stay
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/settings/risk`, {
        standard_integrations: stdText.split('\n').map(s => s.trim()).filter(Boolean),
        integration_count_threshold: parseInt(countThr, 10) || 4,
        integration_hours_threshold: parseFloat(hoursThr) || 0,
        governance_threshold: parseFloat(govThr) || 1.1,
        timeline_threshold: parseFloat(tlThr) || 1.0,
        data_integrity_medium_threshold: parseFloat(diMedThr) || 1.0,
        data_integrity_high_threshold: parseFloat(diHighThr) || 1.2,
      }, { withCredentials: true });
      toast.success(`Risk settings saved — badges refreshed on ${res.data.sweep?.updated ?? 0} estimates`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save risk settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="risk-settings-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Risk Badge Settings</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          The integration risk shows only when an estimate has at least the threshold count of
          <b> non-standard</b> integrations <b>and</b> their combined effort exceeds the hours threshold.
          Standard integrations listed below never trigger the risk.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Integration count threshold</Label>
                <Input type="number" min="1" value={countThr} onChange={e => setCountThr(e.target.value)}
                  className="h-8 mt-1" data-testid="risk-count-threshold-input" />
              </div>
              <div>
                <Label className="text-xs">Integration hours threshold</Label>
                <Input type="number" min="0" value={hoursThr} onChange={e => setHoursThr(e.target.value)}
                  className="h-8 mt-1" data-testid="risk-hours-threshold-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Standard integrations (one per line, excluded from risk)</Label>
              <Textarea value={stdText} onChange={e => setStdText(e.target.value)} rows={6}
                className="mt-1 text-xs font-mono" data-testid="risk-standard-integrations-input" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-600 mb-1">Modifier trigger levels</p>
              <p className="text-[11px] text-slate-400 mb-2">A risk shows when the estimate's modifier value exceeds the level below (1.0 = neutral; higher = worse conditions).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Governance (high risk above)</Label>
                  <Input type="number" step="0.05" min="0.5" value={govThr} onChange={e => setGovThr(e.target.value)}
                    className="h-8 mt-1" data-testid="risk-governance-threshold-input" />
                </div>
                <div>
                  <Label className="text-xs">Timeline pressure (medium risk above)</Label>
                  <Input type="number" step="0.05" min="0.5" value={tlThr} onChange={e => setTlThr(e.target.value)}
                    className="h-8 mt-1" data-testid="risk-timeline-threshold-input" />
                </div>
                <div>
                  <Label className="text-xs">Data integrity (medium risk above)</Label>
                  <Input type="number" step="0.05" min="0.5" value={diMedThr} onChange={e => setDiMedThr(e.target.value)}
                    className="h-8 mt-1" data-testid="risk-di-medium-threshold-input" />
                </div>
                <div>
                  <Label className="text-xs">Data integrity (high risk above)</Label>
                  <Input type="number" step="0.05" min="0.5" value={diHighThr} onChange={e => setDiHighThr(e.target.value)}
                    className="h-8 mt-1" data-testid="risk-di-high-threshold-input" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving} size="sm"
                className="bg-[#0F2040] hover:bg-[#1a3156] text-white" data-testid="risk-settings-save-btn">
                {saving ? 'Saving…' : 'Save Risk Settings'}
              </Button>
              <Button onClick={recalc} disabled={recalcing} size="sm" variant="outline"
                title="Re-evaluates the saved risk badges on every open estimate using the current settings"
                data-testid="risk-recalc-btn">
                {recalcing ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Recalculating…</> : 'Recalculate Risk Badges'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskSettingsCard;
